import React, { useMemo, useState } from 'react';
import Dropdown from '../../../../components/Dropdown';
import { DropdownItem } from '../../../../components/Dropdown/config';
import { FilterConfig, Project, TeamMember, GROUP_STATUS_OPTIONS, DATE_RANGE_PRESETS, getDateRange } from '../../config';

interface FilterBarProps {
  filters: FilterConfig;
  projects: Project[];
  teamMembers: TeamMember[];
  onFilterChange: (newFilters: Partial<FilterConfig>) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  projects,
  teamMembers,
  onFilterChange
}) => {
  const [customDateRange, setCustomDateRange] = useState(false);

  // Determine the current preset based on the filter dates
  const getCurrentPreset = useMemo(() => {
    // Check if current dates match any preset
    
    // Check 7 days
    const sevenDaysRange = getDateRange(7);
    if (filters.dateFrom === sevenDaysRange.from && filters.dateTo === sevenDaysRange.to) {
      return '7';
    }
    
    // Check 30 days
    const thirtyDaysRange = getDateRange(30);
    if (filters.dateFrom === thirtyDaysRange.from && filters.dateTo === thirtyDaysRange.to) {
      return '30';
    }
    
    // Check this week
    const thisWeekRange = getDateRange('thisWeek');
    if (filters.dateFrom === thisWeekRange.from && filters.dateTo === thisWeekRange.to) {
      return 'thisWeek';
    }
    
    // Check this month
    const thisMonthRange = getDateRange('thisMonth');
    if (filters.dateFrom === thisMonthRange.from && filters.dateTo === thisMonthRange.to) {
      return 'thisMonth';
    }
    
    // If no preset matches, it's custom
    return 'custom';
  }, [filters.dateFrom, filters.dateTo]);

  // Sync customDateRange state with computed preset
  React.useEffect(() => {
    setCustomDateRange(getCurrentPreset === 'custom');
  }, [getCurrentPreset]);

  // Create dropdown items for projects
  const projectDropdownItems = useMemo<DropdownItem[]>(() => {
    const items: DropdownItem[] = [
      { id: 'all', label: 'All Projects', value: '' }
    ];
    
    projects.forEach(project => {
      items.push({
        id: project.id.toString(),
        label: `${project.job_number} - ${project.name}`,
        value: project.id.toString()
      });
    });
    
    return items;
  }, [projects]);

  // Create dropdown items for team members (project leads) - Engineers only
  const teamMemberDropdownItems = useMemo<DropdownItem[]>(() => {
    const items: DropdownItem[] = [
      { id: 'all', label: 'All Project Leads', value: '' }
    ];
    
    // Filter to only show Engineers as project leads (teamMembers already filtered to active in parent)
    const engineers = teamMembers.filter(member => member.type === 'Engineer');
    
    engineers.forEach(member => {
      items.push({
        id: member.id.toString(),
        label: `${member.first_name} ${member.last_name}`,
        value: member.id.toString()
      });
    });
    
    return items;
  }, [teamMembers]);

  // Create dropdown items for employees - All team members
  const employeeDropdownItems = useMemo<DropdownItem[]>(() => {
    const items: DropdownItem[] = [
      { id: 'all', label: 'All Employees', value: '' }
    ];
    
    // Show all team members for employee filter
    teamMembers.forEach(member => {
      items.push({
        id: member.id.toString(),
        label: `${member.first_name} ${member.last_name}`,
        value: member.id.toString()
      });
    });
    
    return items;
  }, [teamMembers]);

  // Create dropdown items for entry status
  const statusDropdownItems = useMemo<DropdownItem[]>(() => {
    return GROUP_STATUS_OPTIONS.map(status => ({
      id: status.value,
      label: status.label,
      value: status.value
    }));
  }, []);

  // Create dropdown items for date range presets
  const dateRangeDropdownItems = useMemo<DropdownItem[]>(() => {
    return DATE_RANGE_PRESETS.map(preset => ({
      id: preset.value.toString(),
      label: preset.label,
      value: preset.value.toString()
    }));
  }, []);

  // Handle project filter change
  const handleProjectChange = (value: string | string[]) => {
    const projectId = Array.isArray(value) ? value[0] : value;
    onFilterChange({ 
      projectId: projectId && projectId !== '' ? parseInt(projectId) : null 
    });
  };

  // Handle team member filter change
  const handleTeamMemberChange = (value: string | string[]) => {
    const memberId = Array.isArray(value) ? value[0] : value;
    onFilterChange({ 
      projectLead: memberId && memberId !== '' ? memberId : null 
    });
  };

  // Handle employee filter change
  const handleEmployeeChange = (value: string | string[]) => {
    const employeeId = Array.isArray(value) ? value[0] : value;
    onFilterChange({ 
      employeeId: employeeId && employeeId !== '' ? employeeId : null 
    });
  };

  // Handle status filter change
  const handleStatusChange = (value: string | string[]) => {
    const statusArray = Array.isArray(value) ? value : [value];
    onFilterChange({ entryStatus: statusArray });
  };

  // Handle date range preset change
  const handleDateRangeChange = (value: string | string[]) => {
    const preset = Array.isArray(value) ? value[0] : value;
    
    if (preset === 'custom') {
      setCustomDateRange(true);
      return;
    }
    
    setCustomDateRange(false);
    const dateRange = getDateRange(
      isNaN(Number(preset)) ? preset : Number(preset)
    );
    onFilterChange({
      dateFrom: dateRange.from,
      dateTo: dateRange.to
    });
  };

  // Handle custom date changes
  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ dateFrom: event.target.value });
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ dateTo: event.target.value });
  };

  // Clear all filters
  const handleClearFilters = () => {
    const defaultRange = getDateRange(30);
    onFilterChange({
      projectId: null,
      projectLead: null,
      employeeId: null,
      entryStatus: ['Submitted'],
      dateFrom: defaultRange.from,
      dateTo: defaultRange.to
    });
    setCustomDateRange(false);
  };

  return (
    <div className="filter-bar">
      <div className="filter-row">
        <div className="filter-group">
          <label>Project</label>
          <Dropdown
            items={projectDropdownItems}
            value={filters.projectId?.toString() || ''}
            onChange={handleProjectChange}
            placeholder="All Projects"
            className="project-filter"
            searchable
          />
        </div>

        <div className="filter-group">
          <label>Project Lead</label>
          <Dropdown
            items={teamMemberDropdownItems}
            value={filters.projectLead || ''}
            onChange={handleTeamMemberChange}
            placeholder="All Project Leads"
            className="team-member-filter"
            searchable
          />
        </div>

        <div className="filter-group">
          <label>Employee</label>
          <Dropdown
            items={employeeDropdownItems}
            value={filters.employeeId || ''}
            onChange={handleEmployeeChange}
            placeholder="All Employees"
            className="ta-employee-dropdown"
            searchable
          />
        </div>

        <div className="filter-group">
          <label>Status</label>
          <Dropdown
            items={statusDropdownItems}
            value={filters.entryStatus}
            onChange={handleStatusChange}
            placeholder="All Statuses"
            className="status-filter"
            multiSelect
          />
        </div>

        <div className="filter-group">
          <label>Date Range</label>
          <Dropdown
            items={dateRangeDropdownItems}
            value={getCurrentPreset}
            onChange={handleDateRangeChange}
            placeholder="Select Range"
            className="date-range-filter"
          />
        </div>

        {customDateRange && (
          <>
            <div className="filter-group">
              <label>From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={handleDateFromChange}
                className="date-input"
              />
            </div>

            <div className="filter-group">
              <label>To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={handleDateToChange}
                className="date-input"
              />
            </div>
          </>
        )}

        <div className="filter-actions">
          <button 
            className="clear-filters-btn"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar; 